import clamp from 'clamp';
import * as React from 'react';

import type { EditorContainer } from '@gitbook/slate-react';

import { ColumnWidthsContext } from '../../../utils/contexts';
import { MAX_COLUMN_WIDTH, MIN_COLUMN_WIDTH } from '../../utils';
import { useColumnOffset } from './useColumnOffset';

/**
 * Hook to manage column resizing in tables.
 * Provides handlers and state for resize operations.
 */
export function useColumnResizer({
    columnId,
    tableKey,
    editor,
    initialWidth,
    columnOrder,
}: {
    columnId: string;
    tableKey: string;
    editor: EditorContainer;
    initialWidth?: number;
    columnOrder: string[];
}) {
    // Get column widths from context
    const columnWidths = React.useContext(ColumnWidthsContext);

    // Column's current width from context or fallback
    const colWidth = columnId
        ? (columnWidths.get(columnId) ?? initialWidth ?? MIN_COLUMN_WIDTH)
        : (initialWidth ?? MIN_COLUMN_WIDTH);

    // Local state for drag operation
    const [isDragging, setIsDragging] = React.useState(false);
    const dragOffsetRef = React.useRef(0);
    const dragStartX = React.useRef<number>(0);
    const startWidth = React.useRef<number>(colWidth);

    // For throttling updates with requestAnimationFrame
    const rafIdRef = React.useRef<number | null>(null);
    const pendingWidthRef = React.useRef<number>(colWidth);

    // Resize column in the editor
    const commitResize = React.useCallback(
        (width: number) => {
            editor.change((change) => {
                change.setDataByKey(tableKey, ['view', 'columnWidths', columnId], width, {
                    normalize: false,
                });

                change.setDataByKey(tableKey, ['view', 'useNewSizing'], true, {
                    normalize: false,
                });
            });
        },
        [editor, tableKey, columnId]
    );

    // Compute new width from drag delta, clamped to min/max
    const computeWidth = React.useCallback((delta: number) => {
        return clamp(startWidth.current + delta, MIN_COLUMN_WIDTH, MAX_COLUMN_WIDTH);
    }, []);

    // Get the cumulative left position from the shared hook
    const cumulativeLeftPosition = useColumnOffset({ columnId, columnOrder });

    // Calculate the current width, considering dragging state
    const currentWidth = isDragging ? computeWidth(dragOffsetRef.current) : colWidth;

    // Handle pointer down to start dragging
    const handlePointerDown = React.useCallback(
        (event: React.PointerEvent<HTMLDivElement>) => {
            if (event.button !== 0) return;

            // Stop propagation to prevent text selection
            event.stopPropagation();
            event.preventDefault();

            dragStartX.current = event.clientX;
            startWidth.current = colWidth;
            dragOffsetRef.current = 0;
            setIsDragging(true);

            // Capture pointer for move/up events
            (event.target as HTMLElement).setPointerCapture(event.pointerId);
        },
        [colWidth]
    );

    // Handle dragging the handle
    const handlePointerMove = React.useCallback(
        (event: React.PointerEvent<HTMLDivElement>) => {
            if (!isDragging) return;

            // Prevent text selection
            event.stopPropagation();
            event.preventDefault();

            const deltaX = event.clientX - dragStartX.current;

            // Make it easier to round number: 5px increments when shift key is pressed
            const adjustedDelta = event.shiftKey ? Math.round(deltaX / 5) * 5 : deltaX;

            dragOffsetRef.current = adjustedDelta;
            pendingWidthRef.current = computeWidth(adjustedDelta);

            // Throttle updates with requestAnimationFrame
            if (rafIdRef.current === null) {
                rafIdRef.current = window.requestAnimationFrame(() => {
                    commitResize(pendingWidthRef.current);
                    rafIdRef.current = null;
                });
            }
        },
        [isDragging, computeWidth, commitResize]
    );

    // Handle pointer up to commit final width
    const handlePointerUp = React.useCallback(
        (event: React.PointerEvent<HTMLDivElement>) => {
            if (!isDragging) return;

            (event.target as HTMLElement).releasePointerCapture(event.pointerId);

            // Prevent text selection
            event.stopPropagation();

            if (rafIdRef.current !== null) {
                cancelAnimationFrame(rafIdRef.current);
                rafIdRef.current = null;
            }

            // Commit final width
            const deltaX = event.clientX - dragStartX.current;

            // Apply 5px increments when shift key is pressed for final width as well
            const adjustedDelta = event.shiftKey ? Math.round(deltaX / 5) * 5 : deltaX;

            const finalWidth = computeWidth(adjustedDelta);
            commitResize(finalWidth);

            dragOffsetRef.current = 0;
            setIsDragging(false);
        },
        [isDragging, computeWidth, commitResize]
    );

    return {
        isDragging,
        colWidth,
        currentWidth,
        handlePointerDown,
        handlePointerMove,
        handlePointerUp,
        dragOffset: dragOffsetRef.current,
        cumulativeLeftPosition: cumulativeLeftPosition + currentWidth, // Add current width for resize handle
    };
}
